import { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let authInitialized = false;

    // Function to load user profile
    const loadProfile = async (userId) => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          console.error('Profile fetch error:', error);
          return null;
        }

        return data;
      } catch (err) {
        console.error('Error loading profile:', err);
        return null;
      }
    };

    // Function to handle auth state (used by both initial check and listener)
    const handleAuthState = async (session) => {
      if (!mounted) return;


      if (session?.user) {
        setUser(session.user);
        const profileData = await loadProfile(session.user.id);
        if (mounted) {
          setProfile(profileData);
          setLoading(false);
          authInitialized = true;
        }
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
        authInitialized = true;
      }
    };

    // Check for existing session on mount
    const checkSession = async () => {
      try {

        // Race between getting session and a 5-second timeout
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session check timeout')), 5000)
        );

        const sessionPromise = supabase.auth.getSession();

        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]);

        if (error) {
          console.error('Session check error:', error);
          // Clear potentially corrupt session
          await supabase.auth.signOut();
          if (mounted && !authInitialized) {
            setLoading(false);
            authInitialized = true;
          }
          return;
        }

        await handleAuthState(session);

      } catch (err) {
        console.error('Session check failed:', err);
        // On timeout or error, clear session and show login
        await supabase.auth.signOut();
        if (mounted && !authInitialized) {
          setUser(null);
          setProfile(null);
          setLoading(false);
          authInitialized = true;
        }
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {

      // Only handle if we've finished initializing
      if (authInitialized && mounted) {
        await handleAuthState(session);
      }
    });

    // Start the session check
    checkSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      return { data, error };
    } catch (err) {
      console.error('SignUp error:', err);
      return { data: null, error: err };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      return { data, error };
    } catch (err) {
      console.error('SignIn error:', err);
      return { data: null, error: err };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();

      // Explicitly clear user and profile state
      setUser(null);
      setProfile(null);

      return { error };
    } catch (err) {
      console.error('SignOut error:', err);
      return { error: err };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: new Error('No user logged in') };

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (!error && data) {
        setProfile(data);
      }

      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({ password: newPassword });
      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  };

  return {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    updatePassword,
  };
}